import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { VariantEvaluationService } from '../VariantEvaluation.service';
import { Project } from 'src/app/models/project.model';
import { takeUntil, take } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { RequestService } from 'src/app/services/request.service';
import { AdvancedSettings } from 'src/app/models/AdvancedSettings.model';
import { CloneService } from 'src/app/services/clone.service';
import { AgeGenderDistribution } from 'src/app/models/AgeGenderDistribution.model';
import { MatDialog } from '@angular/material/dialog';
import { TransportModeEnum } from 'src/app/models/enums/TransportModeEnum';
import { ModalDialog } from 'src/app/Modal/modal';
import { TravelMix } from 'src/app/models/TravelMix.model';
import { Region } from 'src/app/models/region.model';
import { dynamicLocaleCompareWithOptions } from 'src/app/helper/sortFunctions';
import { Default } from 'src/app/models/default.model';
import { AgeGenderCompareDialog } from 'src/app/Modal/ageGenderCompare.model';
import { SortAgeGenderDistributionValuesById } from 'src/app/helper/sortAgeGenderDistributionValuesById';
import { userHasFeature } from 'src/app/helper/userHasFeature';
import { AuthService } from 'src/app/services/auth.service';
import { FeatureEnum } from 'src/app/models/enums/FeatureEnum';
import { TrainEvaluationService } from '../TrainEvaluation.service';

@Component({
    selector: 'app-evaluation-advanced-settings',
    templateUrl: './evaluation-advanced-settings.component.html',
    styleUrls: ['./evaluation-advanced-settings.component.scss']
})
export class EvaluationAdvanceSettingsComponent implements OnInit, OnDestroy {
    @Output() onSaveAsProjectDefault: EventEmitter<any> = new EventEmitter();
    @Input() evaluationService: VariantEvaluationService | TrainEvaluationService;

    private destroyed$: Subject<void> = new Subject();
    private currentAgeGenderDistribution: AgeGenderDistribution;

    public regionLabel = 'Region';
    public transportModeLabel = 'Mode of transport';
    public checkBoxSeatReservation = 'Seat reservation is obligatory for the train';
    public comfortLabel = 'Baggage lifting willingness';
    public seatOccupancyRate = 'Varying total seat occupancy rate';
    public standingplaceOccupancyRate = 'Varying degree of standing-room utilisation';
    public personsPerSquareMeterEntranceArea = 'Persons/m² in entrance area';
    public personsPerSquareMeterMultiPurposeArea = 'Persons/m² in multi-purpose area';
    public personsPerSquareMeterAisle = 'Persons/m² in aisle';
    public percent = 'Percent';
    public absolute = 'Absolute';
    public personsBoarding = 'Persons boarding';
    public personsDeboarding = 'Persons deboarding';
    public btnResetRegionDefaults = 'Region Defaults';
    public btnResetRegionDefaultsTitel = 'Reset current advanced settings and use region specific values.';
    public btnResetProjectDefaults = 'Project Defaults';
    public btnResetProjectDefaultsTitel = 'Reset current settings and use values specified on project level.';
    public btnSaveProjectDefaults = 'Save Settings';
    public btnSaveProjectDefaultsTitel = 'Saves the current values of the settings at project level.';
    public currentTransportModeId: number;
    public currentRegionId: number;
    public isReservationDisabled: boolean;
    public showFeatureReservationNeeded: boolean;

    public get advancedSettings() {
        if (this.evaluationService.evaluationFrontEndParameter && this.evaluationService.evaluationFrontEndParameter.advancedSettings) {
            return this.evaluationService.evaluationFrontEndParameter.advancedSettings;
        }
        return undefined;
    }

    constructor(private authService: AuthService, private requestService: RequestService, private cloneService: CloneService, private dialog: MatDialog) {
        this.showFeatureReservationNeeded = userHasFeature(this.authService.getCurrentFeatures(), FeatureEnum.CanOverrideReservationNeededEvaluation);
    }

    ngOnInit(): void {
        this.evaluationService.project
            .pipe(takeUntil(this.destroyed$))
            .subscribe((project: Project) => {
                if (project !== undefined && project !== null) {
                    this.loadAdvancedSettings(project);
                    this.evaluationService.evaluationFrontEndParameter.projectId = project.id;
                } else {
                    this.evaluationService.evaluationFrontEndParameter.travelMixList = new Array<number>();
                    this.evaluationService.evaluationFrontEndParameter.advancedSettings = null;
                    this.evaluationService.evaluationFrontEndParameter.graphicList = null;
                    this.evaluationService.evaluationFrontEndParameter.projectId = null;
                    this.currentAgeGenderDistribution = null;
                    this.evaluationService.travelMixList = null;
                    this.evaluationService.travelMixListFiltered = null;
                    this.evaluationService.passengerAlgorithmGraphicSelected.next(false);
                    this.evaluationService.selectedTravelMixList = new Array<TravelMix>();
                }
            })
    }

    loadAdvancedSettings(project: Project) {
        this.requestService.getAdvancedSettings(project)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((resp: AdvancedSettings) => {

                this.evaluationService.evaluationFrontEndParameter.advancedSettings = resp;
                this.currentAgeGenderDistribution = this.cloneService.deepClone(resp.ageGenderDistribution);
                this.currentTransportModeId = this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId;
                this.currentRegionId = this.evaluationService.evaluationFrontEndParameter.advancedSettings.regionId;
                this.evaluationService.setFilteredGraphics();
                this.setReservationFieldState();
                this.loadTravelMixes(project.regionId, project.transportModeId);
            });
    }

    loadTravelMixes(regionId, transportModeId) {
        this.evaluationService.resetTravelMixList();
        this.requestService.getTravelMixes(regionId, transportModeId)
            .pipe(take(1))
            .subscribe((resp: TravelMix[]) => {
                if (resp !== null) {
                    resp.sort((a, b) => dynamicLocaleCompareWithOptions(a, b, 'name'));
                    this.evaluationService.travelMixList = resp;
                    this.evaluationService.travelMixListFiltered = this.evaluationService.travelMixList;
                }
            });
    }

    getModalConfig(): any {
        const config = {
            data: {
                title: 'Warning',
                message: 'Changing This Option Will Cause Certain Inputs To Be Reseted. Continue?',
                showCancel: true,
                cancelButtonText: 'Cancel',
                saveButtonText: 'Okay'
            }
        };
        return config;
    }

    transportModeChange(transportModeId: number) {
        const dialogRef = this.dialog.open(ModalDialog, this.getModalConfig());

        dialogRef.afterClosed()
            .subscribe(result => {
                this.handleTransportModeResult(result, transportModeId);
            });
    }

    handleTransportModeResult(modalResult: any, transportModeId: number) {
        if (modalResult) {
            this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId = transportModeId;
            this.evaluationService.setFilteredGraphics();
            this.resetToRegionDefaults();
            this.loadTravelMixes(this.evaluationService.evaluationFrontEndParameter.advancedSettings.regionId, this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId);
        }
        else {
            this.currentTransportModeId = this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId;
        }

        this.setReservationFieldState();
    }

    setReservationFieldState() {
        if (this.currentTransportModeId == TransportModeEnum.LongDistanceTransport) {
            this.isReservationDisabled = false;
        }
        else {
            this.isReservationDisabled = true;
            this.evaluationService.evaluationFrontEndParameter.advancedSettings.reservationNeeded = false;
        }
    }

    resetToRegionDefaults() {
        this.setRegionDefaults(this.evaluationService.evaluationFrontEndParameter.advancedSettings.regionId);
    }

    resetToProjectDefaults() {
        this.loadAdvancedSettings(this.evaluationService.project.value);
    }

    reservationStateChange(status: boolean) {
        this.evaluationService.evaluationFrontEndParameter.advancedSettings.reservationNeeded = status;
        this.evaluationService.setFilteredGraphics();
    }

    setRegionDefaults(regionId: number) {
        let region: Region = this.evaluationService.regions.find(r => r.id == regionId);
        let advancedSettingDefaults: Default = region.transportModeDefaults.find(x => x.transportModeId == this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId).default;

        this.evaluationService.evaluationFrontEndParameter.advancedSettings.ageGenderDistribution = region.ageGenderDistribution;
        this.currentAgeGenderDistribution = this.cloneService.deepClone(region.ageGenderDistribution);
        this.evaluationService.evaluationFrontEndParameter.advancedSettings.default = advancedSettingDefaults;
    }

    openAgeGenderMatrix() {
        const dialogRef = this.dialog.open(AgeGenderCompareDialog, this.getAgeGenderDialogConfig());
        // const dialogRef = this.dialog.open(AgeGenderDialog, this.getAgeGenderDialogConfig());

        dialogRef.afterClosed()
            .subscribe(result => {
                if (result) {
                    this.currentAgeGenderDistribution = result.data.ageGenderDistribution;
                    this.evaluationService.evaluationFrontEndParameter.advancedSettings.ageGenderDistribution = this.cloneService.deepClone(this.currentAgeGenderDistribution);
                }
            });
    }

    getAgeGenderDialogConfig() {
        let ageGenderDistribution = this.cloneService.deepClone(this.currentAgeGenderDistribution);
        let ageGenderDistributionComparer = this.cloneService.deepClone(this.evaluationService.regions.filter(f => f.id == this.currentRegionId)[0].ageGenderDistribution);

        ageGenderDistribution.values = SortAgeGenderDistributionValuesById(ageGenderDistribution.values);

        // config object for the age gender dialog
        let config = {
            width: '1480px',
            data: {
                title: 'Age Gender Distribution',
                showCancel: true,
                cancelButtonText: 'Cancel',
                saveButtonText: 'Save',
                ageGender: ageGenderDistribution,
                height: '150px',
                isEditable: false,
                isSumDisplayed: true,
                isCompareMatrixEditable: true,
                isCompareMatrixSumDisplayed: false,
                ageGenderCompareMatrix: ageGenderDistributionComparer
            }
        };
        return config;
    }

    regionChange(regionId: number) {
        const dialogRef = this.dialog.open(ModalDialog, this.getModalConfig());

        dialogRef.afterClosed()
            .subscribe(result => {
                this.handleRegionResult(result, regionId);
            });
    }

    handleRegionResult(modalResult: any, regionId: number) {
        if (modalResult) {
            this.evaluationService.evaluationFrontEndParameter.advancedSettings.regionId = regionId;
            this.setRegionDefaults(regionId);
            this.loadTravelMixes(this.evaluationService.evaluationFrontEndParameter.advancedSettings.regionId, this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId);
        } else {
            this.currentRegionId = this.evaluationService.evaluationFrontEndParameter.advancedSettings.regionId;
        }
    }

    saveAsProjectDefaultDialog() {
        const dialogRef = this.dialog.open(ModalDialog, this.getSaveAsProjectDefaultDialogConfig());

        dialogRef.afterClosed()
            .subscribe(result => {
                if (result) {
                    this.saveAsProjectDefault();
                }
                else {
                    //
                }
            });
    }

    getSaveAsProjectDefaultDialogConfig() {
        let config = {
            data: {
                title: 'Warning',
                message: 'Saving will override existing project settings. Continue?',
                showCancel: true,
                cancelButtonText: 'Cancel',
                saveButtonText: 'Okay'
            }
        };
        return config;
    }

    saveAsProjectDefault() {
        let project: Project = this.cloneService.deepClone(this.evaluationService.project.value);

        project.regionId = this.evaluationService.evaluationFrontEndParameter.advancedSettings.regionId;
        project.transportModeId = this.evaluationService.evaluationFrontEndParameter.advancedSettings.transportModeId;
        project.reservationNeeded = this.evaluationService.evaluationFrontEndParameter.advancedSettings.reservationNeeded;
        project.comfortTypeId = this.evaluationService.evaluationFrontEndParameter.advancedSettings.comfortTypeId;

        if (project.default != null) {
            // map all values to the project default object since there is an existing default object id we don't want to override this
            project.default.personValueFormatId = this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personValueFormatId;
            project.default.personsBoardingDefault = this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsBoardingDefault;
            project.default.personsDeboardingDefault = this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsDeboardingDefault;
            project.default.personsPerSquareMeterEntranceArea = this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsPerSquareMeterEntranceArea;
            project.default.personsPerSquareMeterMultiPurposeArea = this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsPerSquareMeterMultiPurposeArea;
            project.default.personsPerSquareMeterAisle = this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.personsPerSquareMeterAisle;
            project.default.seatOccupancyRateDefault = this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.seatOccupancyRateDefault;
            project.default.standingOccupancyRateDefault = this.evaluationService.evaluationFrontEndParameter.advancedSettings.default.standingOccupancyRateDefault;
        }
        else {
            // clone the default object into project default and set the id to -1 for insert purpose
            project.default = this.cloneService.deepClone(this.evaluationService.evaluationFrontEndParameter.advancedSettings.default);
            project.default.id = -1;
        }

        if (project.ageGenderDistribution != null) {
            project.ageGenderDistribution.values = this.currentAgeGenderDistribution.values;
        }
        else {
            project.ageGenderDistribution = new AgeGenderDistribution();
            project.ageGenderDistribution.id = -1;
            project.ageGenderDistribution.values = this.cloneService.deepClone(this.currentAgeGenderDistribution.values);
        }

        this.requestService.writeProject(project)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(resp => {
                this.evaluationService.project.next(resp);
                this.onSaveAsProjectDefault.emit();
            });
    }

    onKeyFunc(event): boolean {
        const charCode = (event.which) ? event.which : event.keyCode;
        //             101 = e            69 = E            45 = -       
        if (charCode == 101 || charCode == 69 || charCode == 45) {
            return false;
        }
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }
}